import React, { useState, useEffect, useRef } from 'react';
import { useApi } from '../../hooks/useApi';
import './TaxReportsProgressPanel.css';
import { fmtTime } from '../../utils/ruDates';

/**
 * Компонент для отображения прогресса загрузки налоговых отчётов
 * Показывает логи в реальном времени и статистику
 */
export function TaxReportsProgressPanel({ accountId, onClose, onComplete }) {
  const { request } = useApi();

  const [progress, setProgress] = useState(null);
  const [logs, setLogs] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState('idle'); // idle, requesting, downloading, parsing, completed
  const [stats, setStats] = useState({
    total: 0,
    notAvailable: 0,
    requested: 0,
    downloaded: 0,
    parsed: 0,
    errors: 0
  });

  const logsEndRef = useRef(null);

  const addLog = (message, type = 'info') => {
    const timestamp = fmtTime();
    setLogs(prev => [...prev, { timestamp, message, type }]);
  };

  const scrollToBottom = () => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [logs]);

  // Загрузка текущего прогресса
  const loadProgress = async () => {
    try {
      const data = await request(`/api/v1/tax-reports/ozon/v2/${accountId}/progress`);
      setProgress(data);
      setStats({
        total: data.total - (data.not_available || 0),  // Только доступные периоды
        notAvailable: data.not_available || 0,
        requested: data.requested + data.ready,
        downloaded: data.downloaded,
        parsed: data.parsed,
        errors: data.error
      });
    } catch (error) {
      addLog(`Ошибка загрузки прогресса: ${error.message}`, 'error');
    }
  };

  // Автоматическая обработка: запрос → скачивание → парсинг
  const startAutoProcess = async () => {
    setIsProcessing(true);
    setLogs([]);
    addLog('Запуск автоматической загрузки отчётов...', 'info');

    try {
      // ЭТАП 1: Запрос отчётов
      setCurrentStep('requesting');
      addLog('ЭТАП 1: Запрос отчётов у OZON...', 'step');

      const requestResult = await request(
        `/api/v1/tax-reports/ozon/v2/${accountId}/request-batch?start_period=2022-08`,
        { method: 'POST' }
      );

      addLog(
        `Запрошено: ${requestResult.requested}, пропущено: ${requestResult.skipped}, ошибок: ${requestResult.failed}`,
        'success'
      );

      // Показываем детали
      for (const detail of requestResult.details) {
        if (detail.status === 'requested') {
          addLog(`  ${detail.period}: получен код ${detail.report_code}`, 'info');
        } else if (detail.status === 'not_available') {
          addLog(`  ${detail.period}: отчёт недоступен`, 'warning');
        } else if (detail.status === 'error') {
          addLog(`  ${detail.period}: ошибка - ${detail.message}`, 'error');
        }
      }

      if (requestResult.requested === 0) {
        addLog('Нет новых отчётов для запроса', 'info');
        setCurrentStep('completed');
        setIsProcessing(false);
        await loadProgress();
        return;
      }

      // Пауза для формирования отчётов
      addLog('Ожидание формирования отчётов (5 сек)...', 'info');
      await new Promise(resolve => setTimeout(resolve, 5000));

      // ЭТАП 2: Скачивание файлов (с несколькими попытками)
      setCurrentStep('downloading');
      addLog('ЭТАП 2: Скачивание XLSX файлов...', 'step');

      const maxAttempts = 10;
      let downloadResult = null;

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        addLog(`  Попытка ${attempt}/${maxAttempts}...`, 'info');

        downloadResult = await request(
          `/api/v1/tax-reports/ozon/v2/${accountId}/check-and-download`,
          { method: 'POST' }
        );

        addLog(
          `  Готово: ${downloadResult.ready}, скачано: ${downloadResult.downloaded}, ожидание: ${downloadResult.waiting}, ошибок: ${downloadResult.failed}`,
          'info'
        );

        if (downloadResult.waiting === 0) {
          addLog(`Все отчёты обработаны!`, 'success');
          break;
        }

        if (attempt < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 3000));
        }
      }

      // ЭТАП 3: Парсинг файлов
      setCurrentStep('parsing');
      addLog('ЭТАП 3: Парсинг XLSX файлов и сохранение в БД...', 'step');

      const parseResult = await request(
        `/api/v1/tax-reports/ozon/v2/${accountId}/parse`,
        { method: 'POST' }
      );

      addLog(
        `Распарсено: ${parseResult.parsed}, ошибок: ${parseResult.failed}`,
        'success'
      );

      // Показываем детали парсинга
      for (const detail of parseResult.details) {
        if (detail.status === 'parsed') {
          addLog(
            `  ${detail.period}: ${detail.operations} операций, ${detail.compensations} компенсаций`,
            'info'
          );
        } else if (detail.status === 'error') {
          addLog(`  ${detail.period}: ошибка - ${detail.message}`, 'error');
        }
      }

      setCurrentStep('completed');
      addLog('Загрузка завершена!', 'success');

      // Загружаем финальный прогресс
      await loadProgress();

      if (onComplete) {
        onComplete();
      }

    } catch (error) {
      addLog(`Критическая ошибка: ${error.message}`, 'error');
      setCurrentStep('error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Загрузка прогресса при монтировании
  useEffect(() => {
    loadProgress();
  }, [accountId]);

  const getStepStatus = (step) => {
    if (currentStep === step) return 'active';
    const steps = ['idle', 'requesting', 'downloading', 'parsing', 'completed'];
    const currentIndex = steps.indexOf(currentStep);
    const stepIndex = steps.indexOf(step);
    return currentIndex > stepIndex ? 'completed' : 'pending';
  };

  return (
    <div className="tax-progress-panel">
      <div className="tax-progress-header">
        <h2>Загрузка налоговых отчётов</h2>
        <button onClick={onClose} className="tax-progress-close">✕</button>
      </div>

      {/* Статистика */}
      {progress && (
        <div className="tax-progress-stats">
          <div className="stat-item">
            <div className="stat-label">Всего периодов</div>
            <div className="stat-value">{stats.total}</div>
          </div>
          {stats.notAvailable > 0 && (
            <div className="stat-item not-available">
              <div className="stat-label">Недоступно</div>
              <div className="stat-value">{stats.notAvailable}</div>
            </div>
          )}
          <div className="stat-item">
            <div className="stat-label">Запрошено</div>
            <div className="stat-value">{stats.requested}</div>
          </div>
          <div className="stat-item">
            <div className="stat-label">Скачано</div>
            <div className="stat-value">{stats.downloaded}</div>
          </div>
          <div className="stat-item">
            <div className="stat-label">Распарсено</div>
            <div className="stat-value">{stats.parsed}</div>
          </div>
          <div className="stat-item error">
            <div className="stat-label">Ошибок</div>
            <div className="stat-value">{stats.errors}</div>
          </div>
        </div>
      )}

      {/* Индикатор этапов */}
      <div className="tax-progress-steps">
        <div className={`step ${getStepStatus('requesting')}`}>
          <div
            className="step-number"
            style={getStepStatus('requesting') !== 'pending' ? { color: '#ffffff' } : {}}
          >
            1
          </div>
          <div className="step-label">Запрос</div>
        </div>
        <div className="step-connector"></div>
        <div className={`step ${getStepStatus('downloading')}`}>
          <div
            className="step-number"
            style={getStepStatus('downloading') !== 'pending' ? { color: '#ffffff' } : {}}
          >
            2
          </div>
          <div className="step-label">Скачивание</div>
        </div>
        <div className="step-connector"></div>
        <div className={`step ${getStepStatus('parsing')}`}>
          <div
            className="step-number"
            style={getStepStatus('parsing') !== 'pending' ? { color: '#ffffff' } : {}}
          >
            3
          </div>
          <div className="step-label">Парсинг</div>
        </div>
      </div>

      {/* Кнопки управления */}
      <div className="tax-progress-controls">
        <button
          onClick={startAutoProcess}
          disabled={isProcessing}
          className="btn-primary"
        >
          {isProcessing ? 'Обработка...' : 'Запустить скачивание'}
        </button>
        <button
          onClick={loadProgress}
          disabled={isProcessing}
          className="btn-secondary"
        >
          Обновить прогресс
        </button>
      </div>

      {/* Окно логов */}
      <div className="tax-progress-logs">
        <div className="logs-header">Логи</div>
        <div className="logs-content">
          {logs.length === 0 ? (
            <div className="logs-empty">Нажмите "Запустить скачивание" для начала</div>
          ) : (
            logs.map((log, index) => (
              <div key={index} className={`log-entry log-${log.type}`}>
                <span className="log-timestamp">[{log.timestamp}]</span>
                <span className="log-message">{log.message}</span>
              </div>
            ))
          )}
          <div ref={logsEndRef} />
        </div>
      </div>
    </div>
  );
}
